import { z } from 'zod';
import { ISSUE_CATEGORIES, USER_ISSUE_CATEGORIES, ISSUE_PRIORITIES, ISSUE_STATUSES, PROJECT_ROLES, PROJECT_KINDS, GLOBAL_ROLES, ROUTING_MODES, SELECTABLE_SPECIALTIES, TOOLBAR_POSITIONS } from './enums';
import { anchorSchema } from './protocol';

export const objectId = z.string().regex(/^[a-f0-9]{24}$/i, 'Invalid id');

export const loginInput = z.object({ email: z.string().email().max(320), password: z.string().min(1).max(500) });
export const passwordPolicy = z.string().min(6, 'Use at least 6 characters').max(200);
export const acceptInvitationInput = z.object({ name: z.string().trim().min(1).max(120), password: passwordPolicy });
export const forgotPasswordInput = z.object({ email: z.string().email().max(320) });
export const resetPasswordInput = z.object({ token: z.string().min(20).max(200), password: passwordPolicy });
// currentPassword may be omitted only by accounts that have never set one (magic-link clients).
/** An administrator sets a reviewer's password (e.g. for signing in on the website). */
export const setPasswordInput = z.object({ password: passwordPolicy, sendEmail: z.boolean().default(false), projectId: objectId.optional() });
export const changePasswordInput = z.object({ currentPassword: z.string().max(500).optional(), newPassword: passwordPolicy });

/** In-app paths a sign-in link may land on (never another origin). */
export const safeNextPath = z
  .string()
  .max(500)
  .regex(/^\/(portal|admin|issue|r)(\/[A-Za-z0-9_\-./?=&%]*)?$/, 'Invalid destination');
export const magicLinkRequestInput = z.object({ email: z.string().email().max(320), next: safeNextPath.optional() });
export const magicConsumeInput = z.object({ token: z.string().min(20).max(200) });
export const signInLinkInput = z.object({ next: safeNextPath.optional(), sendEmail: z.boolean().default(false) });

export const memberFlagsInput = z.object({
  canUpload: z.boolean().optional(),
  canPublish: z.boolean().optional(),
  canOverrideApproval: z.boolean().optional(),
  isDesignatedApprover: z.boolean().optional(),
});

// A specialty (Developer, QA tester…): a label for team accounts and team members; '' = not set.
export const specialtyInput = z.union([z.literal(''), z.enum(SELECTABLE_SPECIALTIES)]);

export const createInvitationInput = z.object({
  email: z.string().email().max(320),
  globalRole: z.enum(GLOBAL_ROLES),
  // Team accounts only (checked by the API).
  specialty: specialtyInput.default(''),
  projectGrants: z
    // storeIds: Shopify app projects — the stores the person works in.
    .array(z.object({ projectId: objectId, role: z.enum(PROJECT_ROLES), storeIds: z.array(objectId).max(500).optional() }).merge(memberFlagsInput))
    .max(50)
    .default([]),
});

export const updateUserInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  globalRole: z.enum(GLOBAL_ROLES).optional(),
  status: z.enum(['active', 'deactivated']).optional(),
  // The account's specialty (team accounts only, checked by the API); '' clears it.
  specialty: specialtyInput.optional(),
  // Talk-through review (voice comments on the website) for this person. Admin only.
  talkReview: z.boolean().optional(),
  discussions: z.boolean().optional(),
  // A role from Admin → Roles & permissions; null = the default for the account type.
  roleId: objectId.nullable().optional(),
});

export const roleInput = z.object({
  name: z.string().trim().min(1, 'Give the role a name').max(60),
  description: z.string().trim().max(300).default(''),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#4f46e5'),
  kind: z.enum(['team', 'client']),
  permissions: z.array(z.string().max(60)).max(60).default([]),
});
export const updateRoleInput = roleInput.partial().omit({ kind: true });

export const createProjectInput = z.object({
  name: z.string().trim().min(1).max(160),
  clientName: z.string().trim().max(160).default(''),
  description: z.string().max(2000).default(''),
  firstPageName: z.string().trim().max(120).optional(),
  // 'shopify_app': one Shopify app in many stores. Omitted = an ordinary project.
  kind: z.enum(PROJECT_KINDS).optional(),
});

/** A store a Shopify app project's app is installed in: its name and its admin address or myshopify domain. */
export const shopifyStoreInput = z.object({
  shop: z.string().trim().min(1).max(2000),
  name: z.string().trim().max(120).default(''),
  // The store's storefront (optional): a page of it, e.g. https://<name>.myshopify.com/ or the store's own domain.
  storefrontUrl: z.string().trim().max(2000).optional(),
});
export const updateShopifyStoreInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  disabled: z.boolean().optional(),
  // '' removes the storefront (comments made there are kept).
  storefrontUrl: z.string().trim().max(2000).optional(),
  // More addresses the storefront runs on (its own domain, www and without); at most 5 in all.
  storefrontOrigins: z.array(z.string().trim().max(300)).max(5).optional(),
});
export const memberStoresInput = z.object({ storeIds: z.array(objectId).max(500) });
/** SMTP account fields shared by a project's own account and the saved accounts. Empty password = keep the saved one. */
const smtpFields = {
  host: z.string().trim().max(255).default(''),
  port: z.coerce.number().int().min(1).max(65535).default(587),
  secure: z.boolean().default(false),
  user: z.string().trim().max(320).default(''),
  password: z.string().max(500).optional(),
  fromName: z.string().trim().max(120).default(''),
  fromEmail: z.union([z.literal(''), z.string().trim().email().max(320)]).default(''),
};
export const MAIL_MODES = ['default', 'saved', 'custom'] as const;
/**
 * How a project sends email (Project → Settings → Email sending): the server default, a saved SMTP
 * account (Admin → Settings), or its own SMTP account. Without `mode`, `enabled` picks custom/default.
 */
export const projectMailInput = z
  .object({
    mode: z.enum(MAIL_MODES).optional(),
    enabled: z.boolean().optional(),
    accountId: objectId.nullable().optional(),
    accountFromName: z.string().trim().max(120).default(''),
    ...smtpFields,
  })
  .transform((m) => ({ ...m, mode: m.mode ?? (m.enabled ? 'custom' : 'default') }))
  .refine((m) => m.mode !== 'custom' || (m.host && m.fromEmail), { message: 'Enter the SMTP host and the From email to switch on custom sending.' })
  .refine((m) => m.mode !== 'saved' || !!m.accountId, { message: 'Choose a saved SMTP account.' });
/** A saved SMTP account (Admin → Settings → Saved SMTP accounts). */
export const mailAccountInput = z
  .object({ name: z.string().trim().min(1, 'Give this account a name').max(120), ...smtpFields })
  .refine((m) => m.host && m.fromEmail, { message: 'Enter the SMTP host and the From email.' });
/** Copies a project's own SMTP account into the saved accounts. */
export const saveMailAccountInput = z.object({ name: z.string().trim().min(1, 'Give this account a name').max(120) });
export const updateProjectInput = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  clientName: z.string().trim().max(160).optional(),
  description: z.string().max(2000).optional(),
  settings: z
    .object({
      blockApprovalOnOpenQA: z.boolean().optional(),
      maskSelectors: z.array(z.string().max(300)).max(50).optional(),
      defaultReviewDays: z.number().int().min(1).max(90).optional(),
      // New QA issues go to this team member; null = nobody (assign by hand).
      defaultQaAssigneeId: objectId.nullable().optional(),
      // Before / After pictures on this project's comments; autoBefore also takes the "before" one.
      beforeAfter: z.boolean().optional(),
      autoBefore: z.boolean().optional(),
      // Voice discussions across pages (talk while moving through the website, pin after).
      discussAcrossPages: z.boolean().optional(),
      // Zoho Quartz: the Quartz project's name (as Zoho Flow sends it) and its recording page. '' = off.
      quartzProjectName: z.string().trim().max(200).optional(),
      quartzRecordUrl: z
        .string()
        .trim()
        .max(2000)
        .refine((u) => u === '' || /^https:\/\/[^\s]+$/.test(u), { message: 'Paste the Quartz recording link (it starts with https://).' })
        .optional(),
      // "Record with Quartz" in this project's website toolbar (the recording page is set in Admin → Settings).
      quartzOn: z.boolean().optional(),
      // This project's own Quartz recording page and script ('' = the ones in Admin → Settings).
      quartzOwnRecordUrl: z
        .string()
        .trim()
        .max(2000)
        .refine((u) => u === '' || /^https:\/\/[^\s]+$/.test(u), { message: 'Paste the Quartz recording page link (it starts with https://).' })
        .optional(),
      // Quartz's install code or its script address; the server keeps only the script address.
      quartzScript: z.string().max(8000).optional(),
      // The Quartz account (Admin → Settings → Zoho Quartz) this project uses; '' = the default.
      quartzAccountId: z.string().max(40).optional(),
      // Zoho Cliq, sent with the weekly report: a group chat's ID (CT_…) or a channel's unique name. '' = none.
      cliqChannel: z
        .string()
        .trim()
        .max(100)
        .refine((c) => c === '' || /^[A-Za-z0-9_.-]+$/.test(c), { message: 'Paste the group’s chat ID (it starts with CT_) or the channel’s unique name, without spaces.' })
        .optional(),
      // That organization's Cliq webhook token. A new value replaces the saved one; '' removes it.
      cliqToken: z
        .string()
        .trim()
        .max(300)
        .refine((t) => t === '' || /^\S{10,}$/.test(t), { message: 'Paste the whole Cliq webhook token, without spaces.' })
        .optional(),
    })
    .optional(),
});

// sendEmail: tell the person they were added (their way in depends on whether they have a password).
// storeIds: Shopify app projects — the stores the person works in.
export const addMemberInput = z.object({ userId: objectId, role: z.enum(PROJECT_ROLES), sendEmail: z.boolean().default(true), storeIds: z.array(objectId).max(500).optional() }).merge(memberFlagsInput);
/** Add a client to a project in one step: account (if new) + membership + sign-in link. */
export const addClientInput = z.object({
  email: z.string().email().max(320),
  name: z.string().trim().min(1).max(120),
  role: z.enum(['client_reviewer', 'client_approver']),
  isDesignatedApprover: z.boolean().optional(),
  canOverrideApproval: z.boolean().optional(),
  sendEmail: z.boolean().default(false),
  // The new-project wizard adds people first and creates links only when the designs are published.
  createLink: z.boolean().default(true),
  // Password for signing in on the live website; emailed with the website address when sendEmail.
  password: passwordPolicy.optional(),
  // Shopify app projects: the stores the person works in (added to the ones they have).
  storeIds: z.array(objectId).max(500).optional(),
});
// specialty: '' clears it. Only team members can have one (checked by the API).
export const updateMemberInput = z
  .object({
    role: z.enum(PROJECT_ROLES).optional(),
    specialty: specialtyInput.optional(),
    // Permissions on this project only; null = back to the person's role / the default.
    permissions: z.array(z.string().max(60)).max(60).nullable().optional(),
  })
  .merge(memberFlagsInput);

export const createPageInput = z.object({ name: z.string().trim().min(1).max(120), slug: z.string().trim().max(120).optional() });
export const updatePageInput = z.object({ name: z.string().trim().min(1).max(120).optional(), order: z.number().int().min(0).optional() });
export const createOptionInput = z.object({ name: z.string().trim().min(1).max(120), kind: z.enum(['alternative', 'final_combined']).default('alternative') });

export const pageMapInput = z.object({
  entries: z
    .array(
      z.object({
        htmlPath: z.string().min(1).max(1024),
        pageId: objectId.optional(),
        newPageName: z.string().trim().max(120).optional(),
        designOptionId: objectId.optional(),
        newOptionName: z.string().trim().max(120).optional(),
        newOptionKind: z.enum(['alternative', 'final_combined']).optional(),
        updateNote: z.string().trim().max(4000).default(''),
      }),
    )
    .min(1)
    .max(200),
  pickListRef: objectId.optional(),
});

export const publishReleaseInput = z.object({
  reviewerIds: z.array(objectId).max(200).optional(),
  dueAt: z.string().datetime().optional(),
  carryForward: z.array(z.object({ issueId: objectId, revisionId: objectId })).max(2000).default([]),
  fixImportId: objectId.optional(),
  expectedUpdatedAt: z.string().optional(),
});

export const restoreInput = z.object({ mode: z.enum(['page', 'release']), updateNote: z.string().trim().max(4000).default('') });

export const previewLaunchInput = z.object({ mode: z.enum(['embedded', 'top_level']), path: z.string().max(2000).default('/') });

export const createIssueInput = z.object({
  target: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('revision'), revisionId: objectId, releaseId: objectId }),
    z.object({ kind: z.literal('round'), roundId: objectId, url: z.string().url().max(4000) }),
  ]),
  category: z.enum(USER_ISSUE_CATEGORIES),
  priority: z.enum(ISSUE_PRIORITIES).default('medium'),
  body: z.string().trim().min(1).max(10000),
  anchor: anchorSchema,
  url: z.string().max(4000).optional(),
  attachmentIds: z.array(objectId).max(10).default([]),
});

export const updateIssueInput = z.object({
  status: z.enum(ISSUE_STATUSES).optional(),
  priority: z.enum(ISSUE_PRIORITIES).optional(),
  category: z.enum(USER_ISSUE_CATEGORIES).optional(),
  assigneeId: objectId.nullable().optional(),
  expectedStatusVersion: z.number().int().min(0),
  comment: z.string().trim().max(10000).optional(),
});

/** A section picked on the page (Pick mode). */
export const pickTargetInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('revision'), revisionId: objectId, releaseId: objectId }),
  z.object({ kind: z.literal('round'), roundId: objectId, url: z.string().url().max(4000) }),
]);
export const createPickInput = z.object({
  target: pickTargetInput,
  anchor: anchorSchema,
  note: z.string().trim().max(2000).default(''),
  label: z.string().trim().max(160).default(''),
  screenshotId: objectId.optional(),
  // Extra uploaded images, like a comment's attachments.
  attachmentIds: z.array(objectId).max(5).default([]),
});
export const pickReplyInput = z.object({ body: z.string().trim().min(1).max(10000) });
export const updatePickInput = z.object({ note: z.string().trim().max(2000) });

export const replyInput = z.object({
  body: z.string().trim().min(1).max(10000),
  visibility: z.enum(['public', 'internal']).default('public'),
  attachmentIds: z.array(objectId).max(10).default([]),
});

export const verifyInput = z.object({ result: z.enum(['pass', 'fail']), comment: z.string().trim().max(10000).optional(), expectedStatusVersion: z.number().int().min(0) });
export const linkFixInput = z.object({ revisionId: objectId, expectedStatusVersion: z.number().int().min(0), note: z.string().max(4000).optional() });
export const reanchorInput = z.object({ revisionId: objectId.optional(), roundId: objectId.optional(), anchor: anchorSchema, reason: z.string().max(1000).optional() });

export const integrationInput = z.object({
  name: z.string().trim().min(1).max(120),
  origins: z.array(z.string().max(300)).min(1).max(10),
  defaultUrl: z.string().url().max(2000),
  routingMode: z.enum(ROUTING_MODES).default('auto'),
  queryAllowlist: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
  // Show a "Log in to comment" button on the website so reviewers sign in there (no portal needed).
  siteLogin: z.boolean().default(true),
  // Storefront password of a password-protected store ('' removes it).
  storePassword: z.string().max(200).optional(),
  // Where the review toolbar sits on the website (unchanged when not sent).
  toolbarPosition: z.enum(TOOLBAR_POSITIONS).optional(),
  // The registered addresses are one website (a moved domain): comments show on every one of them.
  originsAreOneSite: z.boolean().optional(),
  // Shopify app projects: the app's handle in the admin (admin.shopify.com/store/<store>/apps/<handle>).
  shopifyAppHandle: z.string().trim().max(2000).optional(),
});

/** Checks a website URL as it is entered (does it load? is it a password-protected store?). */
export const siteCheckInput = z.object({ url: z.string().url().max(2000), storePassword: z.string().max(200).optional() });

/** Sign in on the reviewed website itself (the SDK's login panel). */
// shop (Shopify app projects): the store the app page is shown for, from Shopify's ?shop= / host.
const siteShop = z.string().trim().max(300).optional();
export const siteLoginInput = z.object({ publicId: z.string().max(80), email: z.string().trim().email().max(320), password: z.string().min(1).max(200), url: z.string().url().max(4000), shop: siteShop });
export const siteRequestInput = z.object({ publicId: z.string().max(80), name: z.string().trim().min(1, 'Enter your name').max(120), email: z.string().trim().email().max(320), password: passwordPolicy, url: z.string().url().max(4000), shop: siteShop });
export const siteForgotInput = z.object({ publicId: z.string().max(80), email: z.string().trim().email().max(320), url: z.string().url().max(4000), shop: siteShop });
// currentPassword may be left out only in a session opened from a "Forgot password?" email.
export const sitePasswordInput = z.object({ currentPassword: z.string().max(200).optional(), newPassword: passwordPolicy });
export const siteLinkInput = z.object({ publicId: z.string().max(80), email: z.string().trim().email().max(320), url: z.string().url().max(4000), shop: siteShop });

// storeId: Shopify app projects — the store the round is for.
export const roundInput = z.object({ name: z.string().trim().min(1).max(120), deploymentId: z.string().trim().max(200).optional(), storeId: objectId.optional() });

export const liveLaunchInput = z.object({ integrationId: objectId, url: z.string().url().max(4000), roundId: objectId.optional(), storeId: objectId.optional() });

export const sectionInput = z.object({ key: z.string().trim().regex(/^[a-z0-9][a-z0-9-_]{0,59}$/), label: z.string().trim().min(1).max(120), required: z.boolean().default(true), order: z.number().int().min(0).default(0) });
export const sectionMappingInput = z.object({ selector: z.string().trim().min(1).max(2000) });

export const pickListDraftInput = z.object({
  expectedVersion: z.number().int().min(0),
  items: z
    .array(z.object({ sectionId: objectId, designOptionId: objectId, revisionId: objectId, note: z.string().max(2000).default('') }))
    .max(500),
});
export const pickListSubmitInput = z.object({ baseSubmissionNumber: z.number().int().min(0), note: z.string().max(4000).default('') });

export const decisionInput = z.object({
  decision: z.enum(['approve', 'request_changes']),
  note: z.string().max(4000).default(''),
  expectedStateVersion: z.number().int().min(0),
  expectedFeedbackVersion: z.number().int().min(0),
  pickListId: objectId.optional(),
  override: z.object({ reason: z.string().trim().min(10).max(2000) }).optional(),
});

export const completeAssignmentInput = z.object({ note: z.string().max(2000).default('') });

export const exportQuery = z.object({
  format: z.enum(['md', 'json', 'zip']).default('md'),
  releaseId: objectId.optional(),
  revisionId: objectId.optional(),
  roundId: objectId.optional(),
  pageId: objectId.optional(),
  status: z.string().optional(),
  category: z.enum(ISSUE_CATEGORIES).optional(),
  includeInternal: z.coerce.boolean().default(false),
});

export const fixReportSchema = z.object({
  format: z.literal('design-review-fixes/v1'),
  exportId: objectId,
  sourceReleaseId: objectId.optional(),
  sourceBuildId: objectId.optional(),
  fixes: z
    .array(
      z.object({
        issue: z.string().regex(/^RV-\d+$/),
        sourceRevisionId: objectId.optional(),
        summary: z.string().trim().min(1).max(4000),
        files: z.array(z.string().max(1024)).max(100).default([]),
      }),
    )
    .min(1)
    .max(2000),
});
export type FixReport = z.infer<typeof fixReportSchema>;
